import { useState } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { star, starOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useCreateOrderReview } from '../../hooks/useOrders';
import { markActivitiesRead } from '../../services/activityService';
import { useProfileStore } from '../../store/useProfileStore';
import { Modal } from '../ui/Modal';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  activityId?: string;
  onReviewed?: () => void;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  orderId,
  activityId,
  onReviewed,
}) => {
  const { t } = useTranslation('activity');
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState('');
  const reviewMutation = useCreateOrderReview();

  const handleSubmit = async () => {
    await reviewMutation.mutateAsync({ orderId, body: { rating, note: note.trim() || undefined } });
    if (activityId) {
      const profileId = useProfileStore.getState().activeProfile?.id;
      if (profileId) {
        await markActivitiesRead(profileId, [activityId]);
      }
    }
    setRating(0);
    setNote('');
    onReviewed?.();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <h3 className="text-lg font-bold mb-4 pr-8">{t('review.title')}</h3>

      {/* Star rating. Sized in container-query units (cqw) rather than rem so
          the row always fits the popup with margin: the modal width is capped
          by the viewport and can't grow with text, so rem-sized stars would
          otherwise scale into the edges at large text. `containerType` on the
          full-width wrapper makes cqw resolve against the inner content width;
          the clamp keeps stars within a sane px range. */}
      <div className="mb-4" style={{ containerType: 'inline-size' }}>
        <div className="flex justify-center" style={{ gap: '2cqw' }}>
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setRating(value)}
              style={{ padding: '1.5cqw' }}
            >
              <IonIcon
                icon={value <= rating ? star : starOutline}
                className={value <= rating ? 'text-brand-warning' : 'text-muted-foreground'}
                style={{ fontSize: 'clamp(20px, 12cqw, 40px)' }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Optional note */}
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t('review.notePlaceholder')}
        rows={3}
        className="w-full rounded-lg border border-border bg-background text-foreground px-3 py-2 text-sm focus:border-brand-primary focus:outline-none resize-none mb-2"
      />

      <p className="text-xs text-muted-foreground mb-4">
        {t('review.privacyNote')}
      </p>

      {/* Submit */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={rating === 0 || reviewMutation.isPending}
        className="w-full rounded-lg bg-brand-primary py-2.5 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {reviewMutation.isPending ? (
          <IonSpinner name="crescent" className="h-4 w-4" />
        ) : (
          t('review.submit')
        )}
      </button>

      {reviewMutation.isError && (
        <p className="mt-2 text-sm text-brand-danger text-center">
          {t('review.error')}
        </p>
      )}
    </Modal>
  );
};
